import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { intl } from "LocaleWrapper";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import Language from "components/utils/Language";
import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { sailingScheduleSchema, SailingScheduleSchemaInput, SailingScheduleSchemaOutput } from "./utils/sailingSchedule.schema";

import { CreateSailingScheduleDocument } from "__generated__/view/sailingschedules/graphql/mutations.generated";

const SailingScheduleCreate = () => {
	const navigate = useNavigate();

	const [ createSailingSchedule, { loading } ] = useMutation(CreateSailingScheduleDocument);

	const { control, handleSubmit } = useForm<SailingScheduleSchemaInput, unknown, SailingScheduleSchemaOutput>({
		resolver: zodResolver(sailingScheduleSchema),
		defaultValues: {
			schedule: "",
		},
	});

	const save = (data: SailingScheduleSchemaOutput) => {
		createSailingSchedule({
			variables: {
				schedule: data.schedule,
			},
		}).then(() => navigate("/view/sailingschedules"));
	};

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("SAILING_SCHEDULE")}
					<Helmet title={intl.formatMessage({ id: "SAILING_SCHEDULE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/sailingschedules")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12 }}>
					<Card>
						<FormGroup
							label={Language("SAILING_SCHEDULE")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="schedule"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default SailingScheduleCreate;
